import React, { useEffect, useMemo, useRef, useState } from 'react'

export type MobileModeKey = 'publish' | 'people' | 'pages' | 'store' | 'saved'

type ModeRow = {
  key: MobileModeKey
  label: string
  pillLabel: string
  iconBg: string
}

type Props = {
  active: MobileModeKey
  onSelect: (k: MobileModeKey) => void
  modes?: ModeRow[]
}

function CheckIcon() {
  return (
    <svg viewBox="0 0 24 24" className="h-4 w-4 fill-white/90" aria-hidden="true">
      <path d="M9 16.2 4.8 12l-1.4 1.4L9 19 21 7l-1.4-1.4z" />
    </svg>
  )
}

function ChevronDown() {
  return (
    <svg viewBox="0 0 24 24" className="h-4 w-4 fill-white/70" aria-hidden="true">
      <path d="M7 10l5 5 5-5z" />
    </svg>
  )
}

export default function MobileModeDropdownPill({ active, onSelect, modes }: Props) {
  const rows = useMemo<ModeRow[]>(
    () =>
      modes ?? [
        { key: 'publish', label: 'PIXE Publish', pillLabel: 'PIXE PUBLISH', iconBg: 'bg-violet-600' },
        { key: 'people', label: 'Pixe People', pillLabel: 'PIXE PEOPLE', iconBg: 'bg-cyan-500' },
        { key: 'pages', label: 'Pixe Pages', pillLabel: 'PIXE PAGES', iconBg: 'bg-sky-500' },
        { key: 'store', label: 'Pixe Store', pillLabel: 'PIXE STORE', iconBg: 'bg-emerald-500' },
        { key: 'saved', label: 'Saved Pixes', pillLabel: 'SAVED', iconBg: 'bg-amber-500' },
      ],
    [modes]
  )

  const [open, setOpen] = useState(false)
  const pillRef = useRef<HTMLButtonElement | null>(null)
  const [anchorTop, setAnchorTop] = useState<number | null>(null)

  const pillText = useMemo(() => rows.find((r) => r.key === active)?.pillLabel ?? 'PIXE', [active, rows])

  useEffect(() => {
    if (!open) return
    const pill = pillRef.current
    if (pill) {
      const r = pill.getBoundingClientRect()
      setAnchorTop(Math.round(r.bottom + 8))
    }
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.preventDefault()
        setOpen(false)
      }
    }
    document.addEventListener('keydown', onKeyDown)
    return () => document.removeEventListener('keydown', onKeyDown)
  }, [open])

  useEffect(() => {
    if (!open) return
    const onClick = (e: MouseEvent) => {
      const t = e.target as Node
      if (pillRef.current && pillRef.current.contains(t)) return
      setOpen(false)
    }
    document.addEventListener('click', onClick)
    return () => document.removeEventListener('click', onClick)
  }, [open])

  return (
    <div className="relative w-full flex justify-center">
      <button
        ref={pillRef}
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="inline-flex items-center gap-2 rounded-full px-4 py-2 border border-white/15 bg-neutral-950/20 backdrop-blur text-white"
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label="Change mode"
      >
        <span className="text-[13px] font-extrabold tracking-[0.08em]">{pillText}</span>
        <ChevronDown />
      </button>

      {open && (
        <div className="fixed inset-0 z-[70]">
          <div className="absolute inset-0 bg-black/45" onClick={() => setOpen(false)} />
          <div
            className="absolute left-1/2 -translate-x-1/2 w-[300px] max-w-[90vw]"
            style={{ top: (anchorTop ?? 0) ? `${anchorTop}px` : 'calc(var(--nav-h, 56px) + 8px)' }}
            role="menu"
            aria-label="Modes"
          >
            <div className="rounded-2xl border border-white/10 bg-neutral-900/95 backdrop-blur p-2 shadow-2xl">
              <div className="flex items-center justify-between px-2 py-2">
                <div className="text-sm font-extrabold tracking-wide">Modes</div>
                <button
                  type="button"
                  onClick={() => setOpen(false)}
                  className="px-3 py-1.5 rounded-lg bg-white/10 hover:bg-white/15 border border-white/10 text-sm"
                >
                  Close
                </button>
              </div>

              <div className="mt-1 space-y-1">
                {rows.map((r) => {
                  const isSelected = r.key === active
                  const icon = r.key === 'publish' ? 'P' : r.key === 'people' ? 'U' : r.key === 'pages' ? 'B' : r.key === 'store' ? 'S' : 'SV'
                  return (
                    <button
                      key={r.key}
                      type="button"
                      role="menuitem"
                      onClick={() => {
                        onSelect(r.key)
                        setOpen(false)
                      }}
                      className={`w-full flex items-center gap-3 px-3 py-3 rounded-xl border transition ${
                        isSelected
                          ? 'bg-white/10 border-white/25'
                          : 'bg-white/5 border-white/10 hover:bg-white/8'
                      }`}
                    >
                      <div className={`h-7 w-7 rounded-full ${r.iconBg} flex items-center justify-center`}>
                        <span className="text-xs font-black text-white">{icon}</span>
                      </div>
                      <div className="flex-1 text-left text-sm font-semibold text-white">{r.label}</div>
                      {isSelected ? <CheckIcon /> : null}
                    </button>
                  )
                })}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
